import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Building2, Users, Snowflake, Flame } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ZoomableImage } from './ZoomableImage';

export const MenuCardPrestige = ({ dishes }) => {
  const { addToCart } = useCart();

  const handleAddToCart = async (dish) => {
    try {
      await addToCart(dish.dish_id, 1);
      toast.success(`${dish.name} ajouté au panier`);
    } catch (error) {
      toast.error('Connectez-vous pour commander');
    }
  };

  // Separate dishes by formule_type
  const entreprisesDishes = dishes.filter(d => d.formule_type === 'entreprises');
  const particuliersDishes = dishes.filter(d => d.formule_type === 'particuliers');

  // Further separate by temp_type (cold/hot)
  const entreprisesCold = entreprisesDishes.filter(d => d.temp_type === 'cold');
  const entreprisesHot = entreprisesDishes.filter(d => d.temp_type === 'hot');
  const particuliersCold = particuliersDishes.filter(d => d.temp_type === 'cold');
  const particuliersHot = particuliersDishes.filter(d => d.temp_type === 'hot');

  const MenuItem = ({ dish, index }) => (
    <motion.div
      key={dish.dish_id}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="group"
    >
      <div 
        className="flex items-start justify-between py-4 px-4 -mx-4 rounded-xl hover:bg-[#EDE4D4] transition-colors cursor-pointer"
        onClick={() => handleAddToCart(dish)}
        data-testid={`menu-item-${dish.dish_id}`}
      >
        <div className="flex-1 pr-4">
          {dish.image_url && (
            <div className="mb-3 rounded-xl overflow-hidden max-w-md">
              <ZoomableImage
                src={dish.image_url}
                alt={dish.name}
                className="w-full h-auto aspect-[16/9] object-cover"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <h4 className="font-heading text-lg font-semibold text-[#3E2723] group-hover:text-terracotta transition-colors">
              {dish.name}
            </h4>
            {dish.is_featured && (
              <Sparkles className="text-ocre" size={16} />
            )}
          </div>
          <p className="text-[#6D4C41] text-sm mt-1 leading-relaxed">
            {dish.description}
          </p>
          {dish.ingredients && dish.ingredients.length > 0 && (
            <p className="text-[#6D4C41]/60 text-xs mt-2 italic">
              {dish.ingredients.slice(0, 5).join(' • ')}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-sage mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            + Ajouter
          </p>
        </div>
      </div>
      <div className="border-b border-[#D4C4AA]/50 mx-4"></div>
    </motion.div>
  );

  const TempSection = ({ items, sectionTitle, icon: Icon }) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-6">
        <h4 className="font-heading text-base font-medium text-[#6D4C41] mb-3 flex items-center gap-2">
          <Icon size={18} className="text-terracotta/70" />
          {sectionTitle}
          <span className="flex-1 h-[1px] bg-[#D4C4AA]/40"></span>
        </h4>
        <div className="space-y-1">
          {items.map((dish, index) => (
            <MenuItem key={dish.dish_id} dish={dish} index={index} />
          ))}
        </div>
      </div>
    );
  };

  const FormuleBlock = ({ title, subtitle, icon: Icon, coldItems, hotItems, borderColor }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#FAF8F4] rounded-3xl p-6 md:p-8 shadow-card border border-[#D4C4AA]/30 mb-6"
    >
      {/* Decorative corners */}
      <div className="relative">
        <div className={`absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 ${borderColor} rounded-tl-lg`}></div>
        <div className={`absolute -top-3 -right-3 w-6 h-6 border-t-2 border-r-2 ${borderColor} rounded-tr-lg`}></div>
        <div className={`absolute -bottom-3 -left-3 w-6 h-6 border-b-2 border-l-2 ${borderColor} rounded-bl-lg`}></div>
        <div className={`absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 ${borderColor} rounded-br-lg`}></div>

        {/* Header */}
        <div className="text-center mb-6 pb-4 border-b border-[#D4C4AA]/30">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Icon className="text-terracotta" size={24} />
            <h3 className="font-heading text-xl md:text-2xl font-bold text-[#3E2723]">
              {title}
            </h3>
          </div>
          <p className="text-[#6D4C41] text-sm">
            {subtitle}
          </p>
        </div>

        {/* Sections */}
        <TempSection items={coldItems} sectionTitle="Sélection Froide" icon={Snowflake} />
        <TempSection items={hotItems} sectionTitle="Sélection Chaude" icon={Flame} />
      </div>
    </motion.div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Main Title */}
      <div className="text-center mb-10">
        <div className="inline-block">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="w-12 h-[2px] bg-terracotta"></span>
            <Sparkles className="text-terracotta" size={28} />
            <span className="w-12 h-[2px] bg-terracotta"></span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#3E2723]">
            Apéro Prestige
          </h2>
          <p className="text-[#6D4C41] mt-3 max-w-lg mx-auto text-sm">
            Une sélection gastronomique d'exception pour vos événements les plus raffinés
          </p>
        </div>
      </div>

      {/* Formule Entreprises */}
      {entreprisesDishes.length > 0 && (
        <FormuleBlock
          title="Formule Prestige Entreprises"
          subtitle="Réceptions professionnelles, cocktails d'affaires & événements corporate"
          icon={Building2}
          coldItems={entreprisesCold}
          hotItems={entreprisesHot}
          borderColor="border-terracotta/50"
        />
      )}

      {/* Formule Particuliers */}
      {particuliersDishes.length > 0 && (
        <FormuleBlock
          title="Formule Prestige Particuliers"
          subtitle="Célébrations privées, anniversaires & dîners d'exception"
          icon={Users}
          coldItems={particuliersCold}
          hotItems={particuliersHot}
          borderColor="border-sage/50"
        />
      )}

      {/* Footer note */}
      <div className="text-center mt-6 bg-[#FAF8F4]/50 rounded-2xl p-4">
        <p className="text-[#6D4C41]/70 text-sm italic">
          Cliquez sur un plat pour l'ajouter au panier
        </p>
        <p className="text-[#6D4C41]/50 text-xs mt-2">
          Devis personnalisé sur demande • Minimum 10 personnes • Contact : <a href="mailto:contact@racines-epices-dumonde.ch" className="text-terracotta hover:underline">contact@racines-epices-dumonde.ch</a>
        </p>
      </div>
    </div>
  );
};
